import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PublicEvent } from '@arenapulse/shared';
import { ApiClient } from '../lib/api-client';
import { getErrorMessage } from '../lib/error-messages';
import { EventList } from '../components/event-list';
import { CreateEventForm } from '../components/create-event-form';
import { ConfirmDialog } from '../components/confirm-dialog';

export function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);

  const [eventToDelete, setEventToDelete] = useState<PublicEvent | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchEvents = async () => {
    setIsLoading(true);
    setError(null);
    const response = await ApiClient.getEvents();
    if (response.success) {
      // The API returns an array of events
      setEvents(response.data);
    } else {
      if (response.error.code === 'UNAUTHORIZED') {
        window.location.href = `${import.meta.env.VITE_WEBSITE_URL}/login`;
        return;
      }
      setError(getErrorMessage(response.error.code));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleCreateSuccess = () => {
    setIsCreating(false);
    fetchEvents();
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    const response = await ApiClient.deleteEvent(eventToDelete.id);

    setIsDeleting(false);

    if (response.success) {
      setEventToDelete(null);
      // Remove from list without refetching or just refetch
      setEvents(events.filter(e => e.id !== eventToDelete.id));
    } else {
      // For 404s the API will return success: false, and we map it
      setDeleteError(getErrorMessage(response.error.code));
    }
  };

  const handleLogout = async () => {
    await ApiClient.logout();
    window.location.href = `${import.meta.env.VITE_WEBSITE_URL}/login`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-surface shadow shrink-0">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-foreground">ArenaPulse</h1>
            <span className="text-sm font-medium text-muted bg-border px-2 py-1 rounded">Organizer</span>
          </div>
          <div>
            <button
              onClick={handleLogout}
              className="text-sm font-medium text-muted hover:text-foreground"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 w-full ${events.length === 0 && !isLoading && !error && !isCreating ? 'flex flex-col justify-center' : ''}`}>
        <div className="md:flex md:items-center md:justify-between mb-8 shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
              Events
            </h2>
            <p className="mt-1 text-sm text-muted">
              Total events: {isLoading ? '...' : events.length}
            </p>
          </div>
          <div className="mt-4 flex md:ml-4 md:mt-0">
            {!isCreating && (
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="ml-3 inline-flex items-center rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Create Event
              </button>
            )}
          </div>
        </div>

        {isCreating && (
          <CreateEventForm
            onSuccess={handleCreateSuccess}
            onCancel={() => setIsCreating(false)}
          />
        )}

        <EventList
          events={events}
          isLoading={isLoading}
          error={error}
          onDeleteClick={(event) => setEventToDelete(event)}
          onCreateClick={() => setIsCreating(true)}
          onOpenArena={(event) => navigate(`/events/${event.id}`, { state: { event } })}
        />
      </main>

      <ConfirmDialog
        isOpen={eventToDelete !== null}
        title="Delete Event"
        message={deleteError || `Are you sure you want to delete "${eventToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setEventToDelete(null);
          setDeleteError(null);
        }}
        isLoading={isDeleting}
      />
    </div>
  );
}
